import { useState } from "react";
import { FiChevronDown, FiEdit2 } from "react-icons/fi";
import { FaLock, FaUnlock } from "react-icons/fa";
import { DateRangeDisplay } from "components/ui/DateRangeDisplay";
import { useControlDate } from "hooks/useControlDate";
import { getResultadoLabel } from "utils/iniciativa-categoria";

interface ApprovalLogEntry {
  action: string;
  user_id: string;
  user_name: string;
  user_matricula: string;
  timestamp: string;
}

interface Entrega {
  _id: string;
  title: string;
  description: string;
  indicator: {
    nome: string;
    cod: string;
    bloco?: string;
    perspectiva?: string;
  };
  owner: string;
  owner_name: string;
  owner_name_guerra: string;
  owner_funcao: string;
  owner_nome_dependencia: string;
  owner_avatar: any;
  start: number;
  end: number;
  status: string;
  team: Array<{ _id: string; name?: string; code?: string }>;
  evaluator_detail: any[];
  non_evaluator_detail: any[];
  prefixo: number;
  diretoria: string;
  result: string;
  original_manager: string;
  mad: boolean;
  original_manager_detail: any;
  categoryPremio?: string;
  approval_log?: ApprovalLogEntry[];
}

interface CardIniciativaProps {
  entrega: Entrega;
  onAprovar: (id: string) => void;
  onReprovar: (id: string) => void;
  onDesfazer?: (id: string) => void;
  onEditar?: (id: string) => void;
}

export function CardIniciativa({
  entrega,
  onAprovar,
  onReprovar,
  onDesfazer,
  onEditar,
}: CardIniciativaProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const aprovacaoGestor = useControlDate('confirmacao_inscricao_elbb');

  // Status de aprovação dos níveis
  const nivel1Aprovado = entrega.status === "aprovada";
  const nivel1Reprovado = entrega.status === "reprovada";
  const aguardando = entrega.status === "aguardando";

  // Última ação do log de aprovação
  const ultimaAcao = entrega.approval_log?.length
    ? entrega.approval_log[entrega.approval_log.length - 1]
    : null;

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Cabeçalho do card */}
      <div className="space-y-3 text-xl">
        <div>
          <h3 className="font-semibold text-gray-800 mb-1">
            Nome da Iniciativa:
          </h3>
          <p className="text-gray-600">{entrega.title}</p>
        </div>

        <div>
          <h3 className="font-semibold text-gray-800 mb-1">
            Acompanhe o andamento:
          </h3>

          <div className="grid grid-cols-1 gap-4">
            {/* Nível 1 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {nivel1Aprovado ? (
                  <FaUnlock className="text-green-600" />
                ) : (
                  <FaLock className="text-gray-400" />
                )}
                <span className="text-lg text-gray-600">
                  Nível 1 - ({ultimaAcao
                    ? `${ultimaAcao.user_name} - ${ultimaAcao.user_matricula}`
                    : entrega.original_manager_detail?.nome_guerra ||
                      entrega.original_manager_detail?.nome ||
                      "Nome do Gerente Executivo"})
                </span>
              </div>
              {/* Barra de progresso */}
              <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    nivel1Aprovado
                      ? "bg-green-500 w-full"
                      : nivel1Reprovado
                      ? "bg-red-500 w-full"
                      : "bg-gray-300 w-0"
                  }`}
                />
              </div>
              <div className="mt-3">
                {nivel1Aprovado && (
                  <p className="text-base text-green-600 font-semibold">
                    Validada
                  </p>
                )}
                {nivel1Reprovado && (
                  <p className="text-base text-red-600 font-semibold">
                    Não validada
                  </p>
                )}
                {aguardando && aprovacaoGestor && (
                  <p className="text-base text-pink-600 font-semibold">
                    <DateRangeDisplay
                      dataInicio={aprovacaoGestor.dataInicio}
                      dataFim={aprovacaoGestor.dataFim}
                      dateFormat="dd/MM"
                      uppercase={false}
                    />
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo expandido */}
        {isExpanded && (
          <div className="pt-4 space-y-4 border-t mt-4">
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                Perspectiva ECBB:
              </h3>
              <p className="text-gray-600 text-sm">
                {entrega.indicator?.perspectiva || "Não informado"}
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Descrição:</h3>
              <p className="text-gray-700 text-sm">
                {entrega.description || "Sem descrição"}
              </p>
            </div>

            {(() => {
              const labelResultado = getResultadoLabel(entrega.categoryPremio, entrega.indicator, entrega.result);
              if (!labelResultado.mostrar) return null;
              return (
                <div className="bg-green-50 border-l-4 border-green-600 p-4">
                  <p className="font-semibold text-green-800 mb-1">
                    {labelResultado.titulo}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {entrega.result || "Sem resultado informado"}
                  </p>
                </div>
              );
            })()}
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Responsável:</h3>
              <div className="flex items-center gap-3">
                {entrega.owner_avatar?.image?.small?.url ? (
                  <img
                    src={entrega.owner_avatar.image.small.url}
                    alt={entrega.owner_name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                    {entrega.owner_name?.[0] || "?"}
                  </div>
                )}
                <div>
                  <p className="text-gray-800 font-medium">
                    {entrega.owner_name}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {entrega.owner_funcao}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {entrega.owner_nome_dependencia}
                  </p>
                </div>
              </div>
            </div>

            {entrega.team && entrega.team.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">
                  Equipe ({entrega.team.length}):
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {entrega.team.map((member, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm text-gray-600"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                        {member.name?.[0] || "?"}
                      </div>
                      <span>
                        {member.name || member._id}{" "}
                        {member.code && `(${member.code})`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 mb-2">
                Informações adicionais:
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Diretoria:</span>
                  <p className="font-medium text-gray-800">
                    {entrega.diretoria}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Prefixo:</span>
                  <p className="font-medium text-gray-800">{entrega.prefixo}</p>
                </div>
                <div>
                  <span className="text-gray-600">Gerente Original:</span>
                  <p className="font-medium text-gray-800">
                    {entrega.original_manager}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <p className="font-medium text-gray-800 capitalize">
                    {entrega.status}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Botão de expandir/recolher */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-4 w-full text-blue-600 font-bold hover:text-blue-700 transition-colors flex items-center gap-2 text-sm justify-center"
      >
        <FiChevronDown
          className={`w-4 h-4 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
        {isExpanded ? "VER MENOS INFORMAÇÕES" : "VER MAIS INFORMAÇÕES"}
      </button>

      {/* Ações */}
      <div className="mt-6 pt-4 border-t flex flex-col items-center gap-3">
        {onEditar && aguardando && (
          <button
            onClick={() => onEditar(entrega._id)}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-semibold transition-colors"
          >
            <FiEdit2 className="w-4 h-4" />
            EDITAR INICIATIVA
          </button>
        )}
        {aguardando ? (
          <>
            <p className="text-gray-700 font-medium mb-1">
              O que deseja fazer com essa entrega?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => onReprovar(entrega._id)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-lg transition-colors uppercase text-sm"
              >
                NÃO VALIDAR
              </button>
              <button
                onClick={() => onAprovar(entrega._id)}
                className="font-bold py-2 px-6 rounded-lg transition-colors uppercase text-sm bg-green-bb-2 hover:bg-green-bb text-blue-bb"
              >
                VALIDAR
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              className={`w-full py-3 rounded-lg font-bold uppercase text-sm ${
                nivel1Aprovado
                  ? "bg-green-500 text-white cursor-default"
                  : "bg-red-500 text-white cursor-default"
              }`}
              disabled
            >
              {nivel1Aprovado ? "VALIDADA" : "NÃO VALIDADA"}
            </button>
            {onDesfazer && (
              <button
                onClick={() => onDesfazer(entrega._id)}
                className="text-gray-600 hover:text-gray-800 text-sm flex items-center gap-1 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Desfazer ação
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
